import type React from "react";
import { useSelector } from "react-redux";
import { selectCrmList } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmListSelectors";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { CrmListObjectType } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { CrmSyncStatusName } from "../types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { CRM_LIST_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import { StyledCRMListItem, StyledCRMSyncLoadingWrapper, StyledOrangeDot, StyledOrangeDotWrapper } from "./styles";
import { SWReactIcons } from "@similarweb/icons";
import {
    getCrmListName,
    getNotSyncedAllDomainsTooltipContent,
} from "app/pages/sales-intelligence/sub-modules/opportunities/helpers/crmList";
import { CrmIntegrationSyncLoading } from "./CrmIntegrationSyncLoading";
import { PlainTooltip } from "@similarweb/ui-components";
import {
    OpportunityListTypeEnum,
    OpportunityListVariant,
} from "app/pages/sales-intelligence/sub-modules/opportunities/types";
import type { OpportunityListItemProps } from "app/pages/sales-intelligence/sub-modules/opportunities/components/list-items/OpportunityListItem/OpportunityListItem";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

export type CrmIntegrationListProps = {
    crmSyncStatusName: CrmSyncStatusName;
    objectType?: CrmListObjectType;
} & Pick<OpportunityListItemProps, "renderSecondaryContent" | "index">;

export const CrmIntegrationList: React.FC<CrmIntegrationListProps> = (props) => {
    const { crmSyncStatusName, objectType = CrmListObjectType.Accounts } = props;
    const crm = crmSyncStatusName as Integration;
    const { syncedDomainsCount = 0, availableDomainsCount = 0 } =
        useSelector((s) => selectCrmList(s, [crm, objectType])) ?? {};
    const isSyncInProgress = syncedDomainsCount === 0;

    const handleClick = () => {
        if (isSyncInProgress) {return;}

        proNavigator.go(CRM_LIST_PAGE_ROUTE, { crm, objectType });
    };

    return (
        <StyledCRMListItem
            dataAutomationType={OpportunityListVariant.Crm}
            icon={
                <SWReactIcons
                    iconName={`${crm}-colored`}
                    size="md"
                />
            }
            listName={getCrmListName(crm, objectType)}
            listType={OpportunityListTypeEnum.Crm}
            numberOfWebsites={syncedDomainsCount}
            onClick={handleClick}
            renderPrimaryContent={() => {
                if (!isSyncInProgress) {
                    return null;
                }

                return (
                    <StyledCRMSyncLoadingWrapper>
                        <CrmIntegrationSyncLoading />
                    </StyledCRMSyncLoadingWrapper>
                );
            }}
            renderSecondaryContent={() => {
                if (syncedDomainsCount <= availableDomainsCount) {
                    return props.renderSecondaryContent?.();
                }

                return (
                    <FlexRow alignItems="center">
                        <PlainTooltip
                            tooltipContent={getNotSyncedAllDomainsTooltipContent(
                                syncedDomainsCount,
                                availableDomainsCount,
                            )}
                        >
                            <StyledOrangeDotWrapper>
                                <StyledOrangeDot />
                            </StyledOrangeDotWrapper>
                        </PlainTooltip>
                        {props.renderSecondaryContent?.()}
                    </FlexRow>
                );
            }}
        />
    );
};
